import axios from "axios";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { AUTH_TOKEN } from "constants/AuthConstant";
import { notification } from "antd";
import { API_BASE_URL } from "constants/ApiConstant";

const unauthorizedCode = [401];

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  withCredentials: true, // Send cookies when cross-domain requests
});

// Config
const TOKEN_PAYLOAD_KEY = "authorization";

// API Request interceptor, we don't need this because withCredentials is set to true and cookies are sent automatically, considering it as fallback
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN);

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = jwtToken;
    }

    return config;
  },
  (error) => {
    notification.error({
      message: "Error",
    });
    return Promise.reject(error);
  }
);

// API response interceptor, to handle global errors
service.interceptors.response.use(
  (response) => {
    if (response?.config?.responseType === "blob") {
      return response;
    }
    return response.data;
  },

  (error) => {
    const status = error?.response?.status;
    const backendMessage = error?.response?.data?.message;
    const status_code = error?.response?.data?.message_code;
    const backendErrors = error?.response?.data?.errors;

    let notificationParam = {
      message: "",
      description: "",
    };

    if (status_code === 3003) {
      return error?.response?.data;
    }

    let detailedErrors = [];

    const extractErrors = (err, parentKey = "") => {
      let messages = [];

      if (Array.isArray(err)) {
        err.forEach((item) => {
          messages = messages.concat(extractErrors(item, parentKey));
        });
      } else if (typeof err === "object" && err !== null) {
        Object.entries(err).forEach(([key, value]) => {
          // 🔑 always overwrite parentKey with the actual field name
          messages = messages.concat(extractErrors(value, key));
        });
      } else if (typeof err === "string") {
        const braceMatch = err.match(/^\{(.+)\}$/s);
        const cleanErr = braceMatch ? braceMatch[1] : err;
        messages.push(cleanErr);
      }

      return messages;
    };

    if (backendErrors) {
      detailedErrors = extractErrors(backendErrors);
    }

    //Backend-provided message takes priority
    if (backendMessage) {
      notificationParam.message = backendMessage;
      notificationParam.description = detailedErrors.join(", ");
    } else {
      //Predefined fallback messages by status
      const statusMessages = {
        404: {
          message: "Resource Not Found",
          description:
            "The server was unable to locate the requested resource. It may have been moved, deleted, or the URL may be incorrect.",
        },
        500: {
          message: "Internal Server Error",
          description:
            "The server encountered an unexpected issue while processing the request. Please try again later or contact support if the problem continues.",
        },
        508: {
          message: "Request Timeout",
          description:
            "The server was unable to complete the request because it exceeded the allowed processing time. Please try again after some time.",
        },
      };

      //Authentication failure handling
      if (unauthorizedCode.includes(status)) {
        notificationParam.message = "Authentication Failed";
        notificationParam.description =
          "Your authentication credentials are invalid or have expired. Please log in again to continue.";

        // Remove tokens & logout
        localStorage.removeItem(AUTH_TOKEN); // Not required if using cookies, but kept for safety
        store.dispatch(signOutSuccess());
      }

      //Apply fallback messages if available
      if (!notificationParam.message && statusMessages[status]) {
        notificationParam = statusMessages[status];
      }
    }

    ///Show notification only if message exists
    if (notificationParam.message) {
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  }
);

export default service;
